import {TypesCore} from "@dp-instant/interfaces";
import {di, EventDispatcher, lazyGet} from "@dp-instant/core";
import {Building} from "@game/coregame/ecs/entities/Building";
import {GameEvents} from "@game/coregame/GameEvents";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {GameModel} from "@game/coregame/model/GameModel";
import {Population} from "@game/coregame/view/Population";
import {TypesGame} from "@game/TypesGame";
import {ReactionSystem} from "tick-knock";
import {FighterEvent} from "./FighterMovementSystem";

export class GamePlayEvent {
    constructor(public readonly won = false) {
        //
    }
}

export class GamePlaySystem extends ReactionSystem {
    private readonly _freeCounts: Map<PlayerType, number> = new Map();
    private readonly _fullAmount: Map<PlayerType, number> = new Map();
    private readonly _isAnyBuilding: Map<PlayerType, boolean> = new Map();
    private _players: PlayerType[] = [
        PlayerType.Neutral,
        PlayerType.First,
        PlayerType.Second,
        PlayerType.Third,
        PlayerType.Fourth,
        PlayerType.Fifth,
        PlayerType.Sixth,
        PlayerType.Seventh,
        PlayerType.Eighth
    ];
    private _fighterEntityCreated = (data: any) => this.onFighterEnitityHandler(data);

    constructor() {
        super(e => e.has(Population) && e.hasTag(Building.ACTIVE_TAG));
    }

    update(dt = 0) {
        let levelEnded,
            isWon = false;
        for (const type of this._players) {
            this._fullAmount.set(type, this._freeCounts.get(type)!);
            this._isAnyBuilding.delete(type);
        }

        (this.entities as Building[]).forEach(entity => {
            const population = entity.get(Population)!;
            const value = this._fullAmount.get(entity.owner) || 0;
            const count = value + population.current;
            this._fullAmount.set(entity.owner, count);
            this._isAnyBuilding.set(entity.owner, true);
        });
        if (!levelEnded && !this.isAnyBotAlive()) {
            levelEnded = true;
            isWon = true;
        }
        if (!levelEnded && !this.isAlive(PlayerType.First)) {
            levelEnded = true;
            isWon = false;
        }
        // TODO: throttle this routine
        const items: {owner: PlayerType; count: number}[] = [];
        let totalValue = 0;

        this._fullAmount.forEach((v, owner) => {
            if (typeof v !== "undefined") {
                items.push({owner, count: v});
                totalValue += v;
            }
        });

        di.get<EventDispatcher>(TypesCore.dispatcher).emit(GameEvents.STATS_UPDATED, {
            items: items.sort((a, b) => a.owner - b.owner),
            totalValue
        });
        if (levelEnded) {
            this.dispatch(new GamePlayEvent(isWon));
            lazyGet<GameModel>(TypesGame.model)?.endStage(isWon);
        }
    }
    protected isAlive(type: PlayerType) {
        return (this._fullAmount.get(type) || 0) > 0 || this._isAnyBuilding.get(type);
    }
    protected isAnyBotAlive() {
        for (const type of this._players) {
            if (type === PlayerType.Neutral || type === PlayerType.First) {
                continue;
            }
            if (this.isAlive(type)) {
                return true;
            }
        }
        return false;
    }

    protected onFighterEnitityHandler(event: FighterEvent) {
        const {fighter} = event;
        let count = this._freeCounts.get(fighter.owner) || 0;
        switch (event.type) {
            case FighterEvent.FIGHTER_DIED:
                count--;
                break;
            case FighterEvent.FIGHTER_CREATED:
                count++;
                break;
        }
        this._freeCounts.set(fighter.owner, count);
    }

    onAddedToEngine() {
        super.onAddedToEngine();
        this.engine.subscribe(FighterEvent, this._fighterEntityCreated);
    }
    onRemovedFromEngine() {
        this.engine.unsubscribe(FighterEvent, this._fighterEntityCreated);
        this._freeCounts.clear();
        this._fullAmount.clear();
        this._isAnyBuilding.clear();
        super.onRemovedFromEngine();
    }
}
